import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Eye, TrendingUp, Users, Clock, RefreshCw, Loader2,
  ArrowUpRight, ArrowDownRight, DollarSign, Calendar, Target
} from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';
import { leadsAPI } from '../lib/api';
import { toast } from 'sonner';

const CHART_COLORS = {
  primary: 'hsl(204, 94%, 40%)',
  accent: 'hsl(171, 66%, 35%)',
  warning: 'hsl(38, 92%, 50%)',
  muted: 'hsl(220, 12%, 45%)',
};

export default function RevealAnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState('30');

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const res = await leadsAPI.getRevealAnalytics(parseInt(days));
      setAnalytics(res.data);
    } catch {
      toast.error('Failed to load reveal analytics');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);

  if (loading && !analytics) {
    return (
      <div className="space-y-6 p-6" data-testid="reveal-analytics-loading">
        <div className="flex justify-between items-center">
          <div className="space-y-2">
            <div className="h-7 w-48 bg-muted rounded animate-pulse" />
            <div className="h-4 w-64 bg-muted/60 rounded animate-pulse" />
          </div>
          <div className="h-9 w-24 bg-muted rounded animate-pulse" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="p-5 rounded-lg border space-y-2">
              <div className="h-4 w-24 bg-muted/60 rounded animate-pulse" />
              <div className="h-8 w-20 bg-muted rounded animate-pulse" />
              <div className="h-3 w-16 bg-muted/40 rounded animate-pulse" />
            </div>
          ))}
        </div>
        <div className="rounded-lg border p-6 space-y-3">
          <div className="h-5 w-32 bg-muted rounded animate-pulse" />
          <div className="h-48 w-full bg-muted/30 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!analytics) return null;

  const { conversion_rate, credit_reset, daily_trend } = analytics;

  return (
    <div className="space-y-6 p-6" data-testid="reveal-analytics-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Reveal Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track contact reveals, conversion rates, and credit usage
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-[140px]" data-testid="days-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="14">Last 14 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={loadAnalytics}
            disabled={loading}
            data-testid="refresh-analytics-btn"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
      </div>

      {/* Empty-state — no reveals yet (Iter 59c) */}
      {analytics.total_reveals === 0 && !loading && (
        <Card data-testid="reveal-analytics-empty">
          <CardContent className="py-10 text-center">
            <Eye className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-sm font-medium">No reveals yet</p>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              Open any lead in your pipeline and click <span className="font-medium">Reveal Email / Phone</span>. Each reveal costs 1 credit and this dashboard will show conversion from revealed contacts through Closed-Won deals.
            </p>
            <Button size="sm" variant="outline" className="mt-4" onClick={() => window.location.href = '/leads'} data-testid="reveal-analytics-go-pipeline-btn">
              Go to Pipeline
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Reveals"
          value={analytics.total_reveals}
          icon={Eye}
          color="text-[hsl(204,94%,40%)]"
          bgColor="bg-[hsl(204,94%,40%)]/10"
          testId="stat-total-reveals"
        />
        <StatCard
          title="Today"
          value={analytics.reveals_today}
          subtitle={`This week: ${analytics.reveals_this_week}`}
          icon={Calendar}
          color="text-[hsl(171,66%,35%)]"
          bgColor="bg-[hsl(171,66%,35%)]/10"
          testId="stat-reveals-today"
        />
        <StatCard
          title="Reveal → Contact"
          value={`${conversion_rate.reveal_to_contact_rate}%`}
          subtitle={`${conversion_rate.contacted} of ${conversion_rate.total_revealed}`}
          icon={TrendingUp}
          color="text-[hsl(38,92%,50%)]"
          bgColor="bg-[hsl(38,92%,50%)]/10"
          testId="stat-conversion-rate"
        />
        <StatCard
          title="Reveal → Won"
          value={`${conversion_rate.reveal_to_win_rate}%`}
          subtitle={`${conversion_rate.won} deals won`}
          icon={Target}
          color="text-emerald-600"
          bgColor="bg-emerald-50"
          testId="stat-win-rate"
        />
      </div>

      {/* Credit Reset Status */}
      {credit_reset && credit_reset.has_billing && credit_reset.reveal_credits_monthly > 0 && (
        <Card data-testid="credit-reset-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-muted-foreground" />
              Monthly Credit Reset
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center gap-6">
              <div>
                <p className="text-xs text-muted-foreground">Plan</p>
                <p className="text-sm font-medium">{credit_reset.plan_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Monthly Allocation</p>
                <p className="text-sm font-medium">{credit_reset.reveal_credits_monthly} reveals</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Granted This Cycle</p>
                <p className="text-sm font-medium">{credit_reset.reveals_granted_this_cycle || 0} reveals</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Next Reset</p>
                <p className="text-sm font-medium">
                  {credit_reset.days_until_reset != null
                    ? `${credit_reset.days_until_reset} days`
                    : 'N/A'}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Reset Value</p>
                <p className="text-sm font-medium text-emerald-600">
                  +${credit_reset.next_reset_value_usd}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Daily Trend Chart */}
        <Card className="lg:col-span-2" data-testid="daily-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Reveals Over Time</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={daily_trend}>
                  <defs>
                    <linearGradient id="revealGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(210, 9%, 92%)" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(d) => {
                      const date = new Date(d);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                    tick={{ fontSize: 11 }}
                    interval={Math.max(0, Math.floor(daily_trend.length / 8))}
                  />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{
                      borderRadius: 8,
                      border: '1px solid hsl(210, 9%, 92%)',
                      fontSize: 12,
                    }}
                    formatter={(value, name) => [value, name === 'reveals' ? 'Reveals' : 'Cost ($)']}
                    labelFormatter={(label) => new Date(label).toLocaleDateString()}
                  />
                  <Area
                    type="monotone"
                    dataKey="reveals"
                    stroke={CHART_COLORS.primary}
                    fill="url(#revealGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Conversion Funnel */}
        <Card data-testid="conversion-funnel-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Conversion Funnel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FunnelStep
              label="Revealed"
              value={conversion_rate.total_revealed}
              percent={100}
              color="hsl(204, 94%, 40%)"
            />
            <FunnelStep
              label="Contacted"
              value={conversion_rate.contacted}
              percent={conversion_rate.reveal_to_contact_rate}
              color="hsl(171, 66%, 35%)"
            />
            <FunnelStep
              label="Won"
              value={conversion_rate.won}
              percent={conversion_rate.reveal_to_win_rate}
              color="hsl(142, 71%, 45%)"
            />

            <div className="pt-3 border-t space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">This Month</span>
                <span className="font-medium">{analytics.reveals_this_month} reveals</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-muted-foreground">This Week</span>
                <span className="font-medium">{analytics.reveals_this_week} reveals</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Daily Cost Chart */}
      <Card data-testid="daily-cost-chart">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <DollarSign className="h-4 w-4 text-muted-foreground" />
            Daily Reveal Cost
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={daily_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(210, 9%, 92%)" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(d) => {
                    const date = new Date(d);
                    return `${date.getMonth() + 1}/${date.getDate()}`;
                  }}
                  tick={{ fontSize: 11 }}
                  interval={Math.max(0, Math.floor(daily_trend.length / 8))}
                />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid hsl(210, 9%, 92%)',
                    fontSize: 12,
                  }}
                  formatter={(value) => [`$${value.toFixed(2)}`, 'Cost']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString()}
                />
                <Bar dataKey="cost" fill={CHART_COLORS.accent} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function StatCard({ title, value, subtitle, icon: Icon, color, bgColor, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs text-muted-foreground font-medium">{title}</p>
            <p className="text-2xl font-semibold mt-1 tracking-tight">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-0.5">{subtitle}</p>
            )}
          </div>
          <div className={`p-2 rounded-lg ${bgColor}`}>
            <Icon className={`h-4 w-4 ${color}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FunnelStep({ label, value, percent, color }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-xs font-medium">{label}</span>
        <span className="text-xs text-muted-foreground">{value} ({percent}%)</span>
      </div>
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.max(percent, 2)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
